import type { FC, PropsWithChildren } from 'react';
import { createContext, useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/browser';
import { UserDetails } from 'src/api/deinverband-api';
import { enhancedApi } from 'src/api/enhanced-api';
import { useAuthContext, useTenantContext } from 'src/hooks/use-context';
import { useHookMemo } from 'src/hooks/use-hook-memo';
import { updateFeatureFlags } from 'src/libs/feature-flags';
import { mapUserDetailsToProfile } from 'src/mappings/user-details-to-profile';
import {
  Capabilities,
  Capability,
  capabilitiesList
} from 'src/types/capabilities';
import { Profile } from 'src/types/user';
import { formatFullName } from 'src/utils/format';
import { setSession } from './auth/auth-context';

export interface MeContextType {
  profile?: Profile;
  remote?: UserDetails;
  capabilities: Capabilities;
}

export const MeContext = createContext<MeContextType | undefined>(undefined);

const canFull = (profile: Profile | undefined, should: Capability) =>
  !!profile?.tenantAdmin || !!profile?.capabilities.includes(should);
const canSome = (profile: Profile | undefined, should: Capability) =>
  !!profile?.tenantAdmin ||
  !!profile?.capabilities.some(
    (can) => can === should || can.startsWith(`${should}.`)
  );

/**
 * Provide the user's profile information to children
 *
 * NOTE: This component is deployed inside the main app component. This allows
 * updates on logout/when not logged in (i.e. updating Sentry). Therefore, it
 * needs to render children, even when not authenticated (meaning the profile is
 * not available). Children should use the AuthGuard if authentication is
 * required.
 */
export const MeProvider: FC<PropsWithChildren> = (props) => {
  const { children } = props;

  const auth = useAuthContext();
  const {
    style: { adminFeatures, userFeatures }
  } = useTenantContext();

  const query = enhancedApi.useGetApiUsersMeQuery(undefined, {
    skip: !auth.authenticated
  });

  const profile = useMemo(
    () => {
      const profile = mapUserDetailsToProfile(query.data);

      // IMPORTANT: this memo has side effects:
      // update global feature flags before the profile changes,
      // to ensure rendering is done with the correct flags
      updateFeatureFlags(profile?.tenantAdmin ? adminFeatures : userFeatures);

      return profile;
    },
    // feature flags will not change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.data]
  );

  // update the Sentry user information whenever possible
  // (this might execute multiple times during login)
  useEffect(() => {
    if (auth.authenticated) {
      // HACK: store the session for master (token based) login
      if (auth.type === 'api-key' && profile && auth.apiKey) {
        console.log('[me] Setting session for API key login');
        setSession(profile, auth.apiKey);
      }

      Sentry.setUser({
        id: profile?.id,
        email: profile?.email,
        username: formatFullName(profile)
      });
    } else Sentry.setUser(null);
  }, [auth, profile]);

  // log out if the backend fails to retrieve the profile
  useEffect(
    () => {
      if (query.error) {
        Sentry.captureException(query.error);
        auth.signOut('profile-error');
      }
    },
    // execute only on error (sign out is stable)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.error]
  );

  // resolve the capabilities (including nested capabilities)
  const capabilities = useMemo((): Capabilities => {
    const capabilities = Object.fromEntries([
      ...capabilitiesList.map((capability) => [
        capability,
        canFull(profile, capability)
      ]),
      ...capabilitiesList.map((capability) => [
        `${capability}.*`,
        canSome(profile, capability)
      ])
    ]);

    console.info('Resolved capabilities', capabilities);
    return capabilities;
  }, [profile]);

  const context = useHookMemo({
    profile,
    remote: query.data,
    capabilities
  });

  return <MeContext.Provider value={context}>{children}</MeContext.Provider>;
};
